import React, { useState, useEffect, useRef } from "react";
import { TextField, InputAdornment, Box, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";

/**
 * Debounced search box.
 *
 * `compact` is for page headers that sit next to other controls: it drops the
 * decorative "Filter" label (which reads as a second, broken Filter button
 * when a real one is beside it) and uses a shorter field so the row does not
 * wrap. The full-width top bar keeps its original look.
 */
const GlobalSearch = ({ onSearch, compact = false, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const isFirstMount = useRef(true);

  // Debounce logic
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <TextField
      variant="outlined"
      placeholder={placeholder || "Search Student, Teacher, Doc..."}
      fullWidth
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{
        width: "100%",
        "& .MuiOutlinedInput-root": {
          borderRadius: "999px",
          height: compact ? 40 : { xs: 42, sm: 46, md: 50 },
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 10px rgba(16, 24, 40, 0.05)",
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "& fieldset": {
            borderColor: "#e6ecf5",
          },
          "&:hover fieldset": {
            borderColor: "#0d47a1",
          },
          "&.Mui-focused": {
            boxShadow: "0 4px 16px rgba(13, 71, 161, 0.14)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#0d47a1",
            borderWidth: "1px",
          },
        },
        "& .MuiInputAdornment-root": {
          color: "#1976d2",
        },
        "& .MuiInputBase-input": {
          padding: compact ? "6px 4px" : { xs: "8px 4px", sm: "10px 8px" },
          fontSize: compact ? 13 : { xs: 13, sm: 14 },
          textOverflow: "ellipsis",
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: compact ? undefined : (
          <InputAdornment position="end">
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                pl: 1.5,
                ml: 0.5,
                borderLeft: "1px solid #e6ecf5",
              }}
            >
              <Typography
                sx={{
                  color: "grey",
                  fontSize: 14,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                Filter
              </Typography>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default GlobalSearch;
